import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, InputBase, Button } from "@mui/material";
import Chart from "react-apexcharts";
import CommonTable from "../../../common_components/CommonTable";
import { ExportButton } from "../../../common_components/FormInputs";
import { SUMMARY_ROW_POSITIONS, EXPAND_TRIGGERS } from "../../../common_components/tableConstants";
import { CurrencyText } from "./commissionTableShared";
// Self-contained note input: keeps its own draft and only commits on blur, so
// typing never re-renders the PacerTab (which would also re-render its charts).
const PacerNoteInput = React.memo(function PacerNoteInput({ value, onCommit, }) {
    const [draft, setDraft] = React.useState(value);
    const focusedRef = React.useRef(false);
    React.useEffect(() => { if (!focusedRef.current)
        setDraft(value); }, [value]);
    return (_jsx(InputBase, { value: draft, placeholder: "Note", onFocus: () => { focusedRef.current = true; }, onChange: (e) => setDraft(e.target.value), onBlur: (e) => { focusedRef.current = false; if ((e.target.value ?? "") !== (value ?? ""))
            onCommit?.(e.target.value); }, sx: {
            fontSize: "14px", width: "100%", border: "1px solid #D0D5DD", borderRadius: "6px",
            bgcolor: "#fff", px: 1.25, py: 0.75,
            "& input": { p: 0, color: "#27272a" },
            "& input::placeholder": { color: "#98A2B3", opacity: 1 },
        } }));
});
const BLUE = "#2E90FA";
const LIGHT_BLUE = "#BAE0FF";
// Comparison (prior-period) series colours — orange, matching the old PHP portal.
const ORANGE = "#F97316";
const LIGHT_ORANGE = "#FDBA74";
// TEMP: when the compare month has no synced data, synthesise sample bars from the
// primary month so the comparison view can be previewed. Set to false / remove once
// real prior-month data is available.
const DUMMY_COMPARE = false;
// Series names for the first (overlap) chart. Shared between the series data and
// the mounted-hook that hides "Projected Commission" by default so they can't drift.
const PROJECTED_SERIES = "Projected Commission";
const PROJECTED_COMPARE_SERIES = "Projected Commission (Compare)";
const MTD_SERIES = "MTD Commission";
const MTD_COMPARE_SERIES = "MTD Commission (Compare)";
const DAILY_SERIES = "Commission In The Day";
const DAILY_COMPARE_SERIES = "Commission In The Day (Compare)";
// Whether the orange prior-period overlay was left switched on. Persisted alongside
// the page's Pacer compare month so a reload comes back to the same chart view.
const PACER_SHOW_COMPARISON_STORAGE_KEY = "commission_tracker_pacer_show_comparison";
const readStoredShowComparison = () => {
    try {
        return localStorage.getItem(PACER_SHOW_COMPARISON_STORAGE_KEY) === "true";
    }
    catch {
        return false;
    }
};
// "2026-07-01" → "07/01/26"
const shortDate = (iso) => {
    const [y, m, d] = iso.split("-");
    return y && m && d ? `${m}/${d}/${y.slice(2)}` : iso;
};
// "2026-07-01" → "7/1/2026" (tooltip title, matches the old portal).
const fullDate = (iso) => {
    if (!iso)
        return "";
    const [y, m, d] = iso.split("-");
    return y && m && d ? `${Number(m)}/${Number(d)}/${y}` : iso;
};
// Day-of-month (1..31) from a "YYYY-MM-DD" string.
const domOf = (iso) => Number(iso.split("-")[2]);
const currencyAxis = (v) => `$${Math.round(v).toLocaleString()}`;
// Dark-header table look from the PHP dashboard.
const pacerTableSx = {
    border: "1px solid #EAECF0",
    borderRadius: "8px",
    overflow: "hidden",
    "& table": { borderCollapse: "separate", borderSpacing: 0 },
    "& thead th": {
        // background: "#3F3F46 !important",
        color: "#fff !important",
        fontWeight: "700 !important",
        textTransform: "capitalize",
        fontSize: "12px !important",
        letterSpacing: "0.02em",
    },
    "& thead th .reusable-table-sort-icon path, & thead th svg path": { stroke: "#fff" },
    "& th, & td": { borderRight: "1px solid #EAECF0", borderBottom: "1px solid #EAECF0" },
    "& tr > :first-of-type": { borderLeft: "1px solid #EAECF0" },
    "& tfoot td, & tfoot th": { backgroundColor: "#F9FAFB", fontWeight: 700 },
};
const columns = [
    { id: "date", label: "Date", width: "140px", align: "left", sortable: true },
    { id: "mtd_commission", label: "MTD Commission", width: "220px", align: "left", sortable: true },
    { id: "commission_in_day", label: "Commission In The Day", width: "260px", align: "left", sortable: true },
    { id: "projected_commission_pacer", label: "Projected Commission Pacer", width: "300px", align: "left", sortable: true },
    { id: "notes", label: "Notes", width: "auto", align: "left", sortable: false },
];
const PacerTab = ({ pacer, onRefresh, refreshing, onExport, exporting, onSaveNote }) => {
    const days = pacer?.days ?? [];
    // ── Prior-period comparison (charts only) ──
    // A compare series arrives only after the user enables + applies a compare month
    // in the header picker. That surfaces the "Compare with prior periods" button;
    // clicking it draws the orange overlay (OFF by default).
    // A compare month having been applied is enough to surface the button — even
    // when that month has no synced data yet (the overlay just draws empty bars).
    const compare = pacer?.compare ?? null;
    const compareAvailable = !!compare;
    const [showComparison, setShowComparison] = useState(readStoredShowComparison);
    // Compare month cleared → hide the overlay so the button can't get stuck "on".
    // Gated on `pacer` because the prop is null while the tab is still fetching, and
    // clearing then would undo the restored-from-storage value before compare arrives.
    useEffect(() => { if (pacer && !compareAvailable)
        setShowComparison(false); }, [pacer, compareAvailable]);
    // Remember the overlay's on/off state for the next visit.
    useEffect(() => {
        try {
            if (showComparison)
                localStorage.setItem(PACER_SHOW_COMPARISON_STORAGE_KEY, "true");
            else
                localStorage.removeItem(PACER_SHOW_COMPARISON_STORAGE_KEY);
        }
        catch {
            /* ignore storage errors (e.g. private mode) */
        }
    }, [showComparison]);
    // Two independent gates:
    //  - dualTip: as soon as a compare month is applied, the tooltip shows BOTH months.
    //  - overlay: the orange compare bars are drawn only after the button is clicked.
    const dualTip = compareAvailable;
    const overlay = showComparison && compareAvailable;
    const categories = useMemo(() => days.map((d) => shortDate(d.date)), [days]);
    // Compare-month values aligned to the PRIMARY month by day-of-month, so the
    // orange bars sit under the same x position (e.g. compare day-21 under 07/21).
    const cmp = useMemo(() => {
        const round = (v) => (v == null ? null : Math.round(v));
        const realDays = compare?.days ?? [];
        // TEMP: no real prior-month data → build sample compare bars (~75–99% of the
        // primary month, deterministic) so the view is previewable. Remove with DUMMY_COMPARE.
        if (DUMMY_COMPARE && compareAvailable && realDays.length === 0) {
            const month = compare?.month; // "YYYY-MM" of the applied compare month
            const factor = (i) => 0.75 + ((i * 13) % 25) / 100; // 0.75..0.99
            return {
                date: days.map((d) => (month ? `${month}-${String(domOf(d.date)).padStart(2, "0")}` : null)),
                mtd: days.map((d, i) => round((d.mtd_commission ?? 0) * factor(i))),
                projected: days.map((d, i) => round(((d.projected_commission_pacer ?? (d.mtd_commission ?? 0) * 1.6)) * factor(i))),
                inDay: days.map((d, i) => {
                    const v = (/-01$/.test(d.date) && d.commission_in_day == null) ? d.mtd_commission : d.commission_in_day;
                    return round((v ?? 0) * factor(i));
                }),
            };
        }
        const byDom = new Map();
        realDays.forEach((d) => byDom.set(domOf(d.date), d));
        return {
            date: days.map((d) => byDom.get(domOf(d.date))?.date ?? null),
            mtd: days.map((d) => round(byDom.get(domOf(d.date))?.mtd_commission)),
            projected: days.map((d) => round(byDom.get(domOf(d.date))?.projected_commission_pacer)),
            inDay: days.map((d) => {
                const c = byDom.get(domOf(d.date));
                if (!c)
                    return null;
                const v = (/-01$/.test(c.date) && c.commission_in_day == null) ? c.mtd_commission : c.commission_in_day;
                return round(v);
            }),
        };
    }, [days, compare, compareAvailable]);
    // Shared axis config for both charts.
    const xAxis = useMemo(() => ({
        categories,
        labels: { rotate: -45, rotateAlways: true, hideOverlappingLabels: true, trim: false, style: { fontSize: "10px", colors: "#667085" } },
        axisTicks: { show: false }, axisBorder: { show: false },
    }), [categories]);
    const yAxis = { labels: { formatter: currencyAxis, style: { colors: "#EC4899", fontSize: "11px" } } };
    const roundOrNull = (v) => (v == null ? null : Math.round(v));
    // ── Chart 1: MTD Commission (+ Projected pace target) — grouped stacked. ──
    // Per date: a "Current" stack (MTD + Projected) beside a "Compare" stack (compare
    // MTD + compare Projected). Series visibility is toggled by the BUILT-IN legend
    // (MTD/Projected pairs move together — see legendClick), NOT React state — so a
    // toggle never re-renders React / replaces the chart DOM. That React re-render was
    // the bug: it swapped the toggle's DOM node out from under the cursor, so clicks
    // stopped registering until the mouse moved. Projected is hidden on load.
    const mtdChart = useMemo(() => {
        const series = [
            { name: MTD_SERIES, group: "current", data: days.map((d) => roundOrNull(d.mtd_commission)) },
            { name: PROJECTED_SERIES, group: "current", data: days.map((d) => roundOrNull(d.projected_commission_pacer)) },
        ];
        const colors = [BLUE, LIGHT_BLUE];
        if (overlay) {
            series.push({ name: MTD_COMPARE_SERIES, group: "compare", data: cmp.mtd });
            series.push({ name: PROJECTED_COMPARE_SERIES, group: "compare", data: cmp.projected });
            colors.push(ORANGE, LIGHT_ORANGE);
        }
        return { series, colors };
    }, [overlay, days, cmp]);
    // Two-column tooltip for the compare view: current month + compare month, each
    // showing the selected metrics (Commission / Projected). Colour keys off the
    // label so it stays correct regardless of which metrics are visible.
    const dualTooltip = (getCurrent, getCompare) => ({ dataPointIndex }) => {
        const cell = (v) => (v == null ? "-" : currencyAxis(Math.round(v)));
        // Colour lives in a small dot (matching the bar colour); the label + value
        // stay dark so faint bar colours (light blue / light orange) read clearly.
        const dotColor = (primary, label) => {
            const proj = label.toLowerCase().includes("projected");
            return primary ? (proj ? LIGHT_BLUE : BLUE) : (proj ? LIGHT_ORANGE : ORANGE);
        };
        const rowHtml = (primary, label, v) => `
                <div style="display:flex;align-items:center;gap:8px;padding:4px 14px;">
                    <span style="width:9px;height:9px;border-radius:3px;background:${dotColor(primary, label)};display:inline-block;flex:0 0 auto;"></span>
                    <span style="color:#fff;font-size:13px;">${label}: <b>${cell(v)}</b></span>
                </div>`;
        const col = (title, primary, rows) => `
                <div style="min-width:210px;padding:0px 0;${primary ? "" : "border-left:1px solid rgba(255,255,255,0.15);"}">
                    <div style="font-weight:700;color:#fff;font-size:12px;padding:6px 14px 6px;margin-bottom:0px;">${title}</div>
                    ${rows.map(([label, v]) => rowHtml(primary, label, v)).join("")}
                </div>`;
        const cDate = cmp.date[dataPointIndex];
        return `<div id="pacer-dark-tooltip" style="display:flex;font-size:12px;border-radius:10px;background:rgba(11,15,25,0.85);box-shadow:0 8px 24px rgba(0,0,0,0.32);font-family:Inter,system-ui,sans-serif;">
                ${col(fullDate(days[dataPointIndex]?.date), true, getCurrent(dataPointIndex))}
                ${cDate ? col(fullDate(cDate), false, getCompare(dataPointIndex)) : ""}
            </div>`;
    };
    const mtdOptions = useMemo(() => {
        // One legend label toggles BOTH its current and compare series.
        const partnerOf = {
            [MTD_SERIES]: MTD_COMPARE_SERIES,
            [MTD_COMPARE_SERIES]: MTD_SERIES,
            [PROJECTED_SERIES]: PROJECTED_COMPARE_SERIES,
            [PROJECTED_COMPARE_SERIES]: PROJECTED_SERIES,
        };
        // Dual tooltip (both months) as soon as compare is applied; else single month.
        const mtdDual = dualTooltip((i) => [["Commission", days[i]?.mtd_commission], ["Projected Commission", days[i]?.projected_commission_pacer]], (i) => [["Commission", cmp.mtd[i]], ["Projected Commission", cmp.projected[i]]]);
        return {
            chart: {
                type: "bar", stacked: true, toolbar: { show: false }, zoom: { enabled: false }, fontFamily: "inherit",
                events: {
                    // Projected (and its compare) hidden on load → MTD-only by default.
                    mounted: (ctx) => {
                        ctx.hideSeries(PROJECTED_SERIES);
                        if (overlay)
                            ctx.hideSeries(PROJECTED_COMPARE_SERIES);
                    },
                    // Keep each metric's current + compare series in lock-step.
                    legendClick: (ctx, seriesIndex, opts) => {
                        const name = opts?.config?.series?.[seriesIndex]?.name ?? opts?.globals?.seriesNames?.[seriesIndex];
                        const partner = name ? partnerOf[name] : undefined;
                        if (partner && overlay)
                            ctx.toggleSeries(partner);
                    },
                },
            },
            colors: mtdChart.colors,
            dataLabels: { enabled: false },
            stroke: { show: false },
            grid: { borderColor: "#F2F4F7", strokeDashArray: 3 },
            legend: { position: "bottom", markers: { size: 6 }, fontSize: "12px", onItemClick: { toggleDataSeries: true } },
            plotOptions: { bar: { columnWidth: "70%", borderRadius: 2 } },
            xaxis: xAxis,
            yaxis: yAxis,
            tooltip: {
                shared: true, intersect: false,
                custom: dualTip
                    ? mtdDual
                    : (({ dataPointIndex }) => {
                        const d = days[dataPointIndex];
                        const title = fullDate(d?.date);
                        const cell = (v) => (v == null ? "-" : currencyAxis(Math.round(v)));
                        const row = (color, label, v) => `<div style="display:flex;align-items:center;gap:6px;">
                                <span style="width:8px;height:8px;border-radius:50%;background:${color};display:inline-block;flex-shrink:0;"></span>
                                <span style="color:#fff;font-size:13px;">${label}: <b>${cell(v)}</b></span>
                            </div>`;
                        return `<div id="pacer-dark-tooltip" style="padding:0px 0px;font-size:12px;min-width:200px;border-radius:10px;background:rgba(11,15,25,0.85);box-shadow:0 8px 24px rgba(0,0,0,0.32);font-family:Inter,system-ui,sans-serif;">
                            <div style="font-weight:700;color:#fff;margin-bottom:0px;background: rgba(0, 0, 0, .7);padding:6px;">${title}</div>
                            ${row(BLUE, MTD_SERIES, d?.mtd_commission)}
                            ${row(LIGHT_BLUE, PROJECTED_SERIES, d?.projected_commission_pacer)}
                        </div>`;
                    }),
            },
        };
    }, [overlay, dualTip, mtdChart.colors, xAxis, yAxis, days, cmp]);
    // ── Chart 2: Commission In The Day — grouped stacked (Current vs Compare). ──
    // The first day of the month has no prior-day delta, so its "commission in the
    // day" is null (blank). For the chart, fall back to that day's MTD commission
    // so the first bar isn't missing (the day's commission == its MTD on day 1).
    const currentInDay = useMemo(() => days.map((d) => {
        const v = (/-01$/.test(d.date) && d.commission_in_day == null) ? d.mtd_commission : d.commission_in_day;
        return v == null ? null : Math.round(v);
    }), [days]);
    const dailyChart = useMemo(() => {
        const series = [
            { name: DAILY_SERIES, group: "current", data: currentInDay },
        ];
        const colors = [BLUE];
        if (overlay) {
            series.push({ name: DAILY_COMPARE_SERIES, group: "compare", data: cmp.inDay });
            colors.push(ORANGE);
        }
        return { series, colors };
    }, [overlay, currentInDay, cmp]);
    const dailyOptions = useMemo(() => ({
        chart: { type: "bar", stacked: true, toolbar: { show: false }, zoom: { enabled: false }, fontFamily: "inherit" },
        colors: dailyChart.colors,
        plotOptions: { bar: { columnWidth: "60%", borderRadius: 2 } },
        dataLabels: { enabled: false },
        grid: { borderColor: "#F2F4F7", strokeDashArray: 3 },
        legend: { position: "bottom", markers: { size: 6 }, fontSize: "12px", onItemClick: { toggleDataSeries: true } },
        xaxis: xAxis,
        yaxis: yAxis,
        // Dual tooltip as soon as compare is applied (bars still gated on the button).
        tooltip: dualTip
            ? {
                shared: true, intersect: false,
                custom: dualTooltip((i) => [["Commission In The Day", currentInDay[i]]], (i) => [["Commission In The Day", cmp.inDay[i]]]),
            }
            : {
                shared: true, intersect: false,
                custom: (({ dataPointIndex }) => {
                    const title = fullDate(days[dataPointIndex]?.date);
                    const v = currentInDay[dataPointIndex];
                    const cell = (val) => (val == null ? "-" : currencyAxis(Math.round(val)));
                    return `<div id="pacer-dark-tooltip" style="font-size:12px;min-width:200px;border-radius:10px;background:rgba(11,15,25,0.85);box-shadow:0 8px 24px rgba(0,0,0,0.32);font-family:Inter,system-ui,sans-serif;">
                        <div style="font-weight:700;color:#fff;margin-bottom:0px;background: rgba(0, 0, 0, .7);padding:6px;">${title}</div>
                        <div style="display:flex;align-items:center;gap:6px;">
                            <span style="width:8px;height:8px;border-radius:50%;background:${BLUE};display:inline-block;flex-shrink:0;"></span>
                            <span style="color:#fff;font-size:13px;">${DAILY_SERIES}: <b>${cell(v)}</b></span>
                        </div>
                    </div>`;
                }),
            },
    }), [dailyChart.colors, dualTip, xAxis, yAxis, currentInDay, cmp, days]);
    // ── Table ──
    const renderCell = (row, col) => {
        switch (col.id) {
            case "date":
                return _jsx(Box, { sx: { px: 1 }, children: _jsx(Typography, { sx: { fontSize: "14px !important", color: "#27272a", whiteSpace: "nowrap" }, children: shortDate(row.date) }) });
            case "mtd_commission":
                return row.mtd_commission == null ? "-" : _jsx(CurrencyText, { value: row.mtd_commission });
            case "commission_in_day":
                return row.commission_in_day == null ? "-" : _jsx(CurrencyText, { value: row.commission_in_day });
            case "projected_commission_pacer":
                return row.projected_commission_pacer == null ? "-" : _jsx(CurrencyText, { value: row.projected_commission_pacer });
            case "notes":
                return _jsx(PacerNoteInput, { value: row.notes ?? "", onCommit: (v) => onSaveNote?.(row.date, v) });
            default: return null;
        }
    };
    // Footer totals = sum of each column across all day rows (not the last day's
    // cumulative value). Null cells (e.g. day-1 "commission in the day") count as 0.
    const columnSums = useMemo(() => days.reduce((acc, d) => {
        acc.mtd_commission += Number(d.mtd_commission) || 0;
        acc.commission_in_day += Number(d.commission_in_day) || 0;
        acc.projected_commission_pacer += Number(d.projected_commission_pacer) || 0;
        return acc;
    }, { mtd_commission: 0, commission_in_day: 0, projected_commission_pacer: 0 }), [days]);
    const renderSummaryRow = (_r, col) => {
        switch (col.id) {
            case "date": return _jsx(Box, { sx: { px: 1 }, children: _jsx(Typography, { sx: { fontWeight: 600, color: "#181d27", fontSize: "14px" }, children: "Total" }) });
            case "mtd_commission": return _jsx(CurrencyText, { value: columnSums.mtd_commission, bold: true });
            case "commission_in_day": return _jsx(CurrencyText, { value: columnSums.commission_in_day, bold: true });
            case "projected_commission_pacer": return _jsx(CurrencyText, { value: columnSums.projected_commission_pacer, bold: true });
            default: return "";
        }
    };
    // No synced data for the month → show a simple empty state (not a blank chart).
    if (days.length === 0) {
        return (_jsx(Box, { sx: {
                bgcolor: "#fff",
                border: "1px solid #EAECF0",
                borderRadius: "8px",
                py: 8,
                px: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }, children: _jsx(Typography, { sx: { color: "#667085", fontSize: "14px !important", fontWeight: 500 }, children: "No records available." }) }));
    }
    return (_jsxs(Box, { sx: { display: "flex", flexDirection: "column", gap: 1.5 }, children: [_jsx(Box, { sx: {
                    bgcolor: "#fff", border: "1px solid #EAECF0", borderRadius: "8px", p: 1.5,
                    // Dark custom tooltip supplies its own panel (background/radius/shadow) —
                    // the wrapper just needs to get out of the way, not paint a white card.
                    "& .apexcharts-tooltip": {
                        border: "none !important",
                        boxShadow: "none !important",
                        background: "transparent !important",
                    },
                }, children: _jsx(Chart, { options: mtdOptions, series: mtdChart.series, type: "bar", height: 300 }, `mtd-${overlay}`) }), compareAvailable && (_jsx(Box, { sx: { display: "flex", justifyContent: "flex-end", mt: -0.5 }, children: _jsx(Button, { onClick: () => setShowComparison((v) => !v), variant: "outlined", startIcon: _jsx("span", { style: { letterSpacing: "2px", fontWeight: 700, lineHeight: 1 }, children: "\u00B7\u00B7\u00B7" }), sx: {
                        textTransform: "none", fontWeight: 600, borderRadius: "8px", height: "36px", px: 2,
                        borderStyle: "dashed", whiteSpace: "nowrap",
                        borderColor: overlay ? ORANGE : "#D0D5DD",
                        color: overlay ? ORANGE : "#475467",
                        bgcolor: "#fff",
                        "&:hover": { borderStyle: "dashed", borderColor: overlay ? ORANGE : "#98A2B3", bgcolor: "#F9FAFB" },
                    }, children: overlay ? "Hide comparison" : "Compare with prior periods" }) })), _jsx(Box, { sx: {
                    bgcolor: "#fff", border: "1px solid #EAECF0", borderRadius: "8px", p: 1.5,
                    // Dark custom tooltip supplies its own panel (background/radius/shadow) —
                    // the wrapper just needs to get out of the way, not paint a white card.
                    "& .apexcharts-tooltip": {
                        border: "none !important",
                        boxShadow: "none !important",
                        background: "transparent !important",
                    },
                }, children: _jsx(Chart, { options: dailyOptions, series: dailyChart.series, type: "bar", height: 260 }, `daily-${overlay}`) }), _jsx(Box, { sx: { display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1, pr: 2 }, children: days.length > 0 &&
                    _jsx(ExportButton, { onClick: () => onExport?.(), loading: exporting }) }), _jsx(Box, { className: "table-container", sx: { ...pacerTableSx, position: "relative" }, children: _jsx(CommonTable, { columns: columns, rows: days, renderCell: renderCell, expandTrigger: EXPAND_TRIGGERS.NONE, enableColumnActions: false, enableColumnReorder: false, enableColumnPinning: false, enableColumnFilters: false, enableColumnVisibility: false, enableSelection: false, enableSummaryRow: days.length > 0, renderSummaryRow: days.length > 0 ? renderSummaryRow : undefined, summaryRowPosition: SUMMARY_ROW_POSITIONS.BOTTOM, isLoading: false, stickyHeader: true, maxHeight: "calc(100vh - 220px)", emptyMessage: "No pacer data available." }) })] }));
};
export default PacerTab;
